import type React from 'react'
import { useIntl } from 'react-intl'

import { InfoIcon, MicIcon, MicOffIcon, SquareIcon, Trash2Icon } from 'lucide-react'

import { Button } from '@/components/atoms/Button'
import { formatVoiceClipDuration, VoiceClipPlayer } from '@/components/molecules/VoiceClipPlayer'
import { VOICE_CLIP_MAX_DURATION_MS, type VoiceRecorder, type VoiceRecording } from '@/hooks/useVoiceRecorder'
import { cn } from '@/lib/utils'

export interface VoiceRecorderPanelProps {
  readonly recorder: Pick<VoiceRecorder, 'state' | 'sending' | 'sendFailed' | 'stop' | 'discard' | 'send'>
  readonly onSend?: (recording: VoiceRecording) => Promise<void>
  readonly size?: 'default' | 'sm'
  readonly className?: string
}

const buttonSize = { default: '', sm: 'h-8 px-4 text-sm' }

export const VoiceRecorderPanel: React.FC<VoiceRecorderPanelProps> = ({
  recorder,
  onSend,
  size = 'default',
  className,
}) => {
  const intl = useIntl()
  const { state, sending, sendFailed, stop, discard, send } = recorder

  if (state.status === 'idle') return null

  const elapsedMs = state.status === 'recording' ? state.elapsedMs : 0
  const elapsedLabel = formatVoiceClipDuration(elapsedMs)

  return (
    <div className="flex flex-col gap-4">
      <div
        className={cn(
          'flex min-h-[72px] items-center gap-3 rounded-[4px] border border-gray-300 px-4 py-2 text-base',
          className
        )}>
        {state.status === 'preview' ? (
          <VoiceClipPlayer src={state.recording.url} durationMs={state.recording.durationMs} isRecording />
        ) : (
          <div
            role="status"
            aria-label={intl.formatMessage({ id: 'message.voice.recordingStatus' }, { elapsed: elapsedLabel })}
            className="flex grow items-center gap-3">
            <span
              aria-hidden="true"
              className="mx-1 size-2.5 shrink-0 rounded-full bg-red-600 ring-[5px] ring-red-600/25"
            />
            <span className="font-bold">{intl.formatMessage({ id: 'message.voice.recording' })}</span>
            <span className="tabular-nums">{elapsedLabel}</span>
            <div
              role="progressbar"
              aria-label={intl.formatMessage({ id: 'message.voice.progress' })}
              aria-valuemin={0}
              aria-valuemax={VOICE_CLIP_MAX_DURATION_MS / 1000}
              aria-valuenow={Math.floor(elapsedMs / 1000)}
              className="h-2 max-w-80 grow overflow-hidden rounded-sm bg-gray-200">
              <div
                className="h-full rounded-sm bg-teal-500"
                style={{ width: `${Math.min(elapsedMs / VOICE_CLIP_MAX_DURATION_MS, 1) * 100}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 tabular-nums">
              / {formatVoiceClipDuration(VOICE_CLIP_MAX_DURATION_MS)}
            </span>
          </div>
        )}
      </div>
      {sendFailed && (
        <p role="alert" className="text-sm text-red-700">
          {intl.formatMessage({ id: 'message.voice.sendFailed' })}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {state.status === 'preview' ? (
          <>
            <Button
              variant="primary"
              className={buttonSize[size]}
              loading={sending}
              disabled={!onSend}
              onClick={async () => {
                if (onSend) await send(state.recording, onSend)
              }}>
              {intl.formatMessage({ id: 'message.voice.send' })}
            </Button>
            <Button className={buttonSize[size]} disabled={sending} onClick={discard}>
              <Trash2Icon className="size-4" aria-hidden="true" />
              {intl.formatMessage({ id: 'message.voice.delete' })}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="primary"
              className={buttonSize[size]}
              disabled={state.status === 'starting'}
              onClick={stop}>
              <SquareIcon className="size-3 fill-white" aria-hidden="true" />
              {intl.formatMessage({ id: 'message.voice.stop' })}
            </Button>
            <Button className={buttonSize[size]} onClick={discard}>
              {intl.formatMessage({ id: 'message.voice.cancel' })}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

export interface VoiceMessageButtonProps {
  readonly recorder: Pick<VoiceRecorder, 'state' | 'start' | 'isSupported'>
  readonly disabled?: boolean
  readonly size?: 'default' | 'sm'
}

export const VoiceMessageButton: React.FC<VoiceMessageButtonProps> = ({ recorder, disabled, size = 'default' }) => {
  const intl = useIntl()
  const { state, isSupported, start } = recorder
  const error = state.status === 'idle' ? state.error : undefined
  return (
    <>
      <Button
        className={buttonSize[size]}
        disabled={disabled || !isSupported}
        aria-describedby={isSupported ? undefined : 'voice-message-unsupported'}
        onClick={start}>
        {isSupported ? (
          <MicIcon className="size-4" aria-hidden="true" />
        ) : (
          <MicOffIcon className="size-4" aria-hidden="true" />
        )}
        {intl.formatMessage({ id: 'message.voice.record' })}
      </Button>
      {!isSupported && (
        <p id="voice-message-unsupported" className="ml-1 flex items-center gap-1.5 text-sm text-gray-600">
          <InfoIcon className="size-3.5" aria-hidden="true" />
          {intl.formatMessage({ id: 'message.voice.unsupported' })}
        </p>
      )}
      {error && (
        <p role="alert" className="basis-full text-sm text-red-700">
          {intl.formatMessage({ id: `message.voice.error.${error}` })}
        </p>
      )}
    </>
  )
}
